import classNames from 'classnames';
import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useId, useState } from 'react';
import styles from './TextInput.module.scss';

type TTextInputVariant = 'standard' | 'gray' | 'borderless';
type TTextInputSize = 'xs' | 'sm' | 'md';

interface ITextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  labelSuffix?: ReactNode;
  helperText?: string;
  error?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  variant?: TTextInputVariant;
  size?: TTextInputSize;
  fullWidth?: boolean;
  showCharCount?: boolean;
  containerClassName?: string;
  wrapperClassName?: string;
  defaultValue?: string | number | readonly string[];
  ref?: Ref<HTMLInputElement>;
}

export function TextInput({
  label,
  labelSuffix,
  helperText,
  error = false,
  startAdornment,
  endAdornment,
  variant = 'standard',
  size = 'md',
  fullWidth = true,
  showCharCount = false,
  className,
  containerClassName,
  wrapperClassName,
  disabled,
  maxLength,
  value,
  defaultValue,
  onChange,
  ref: forwardedRef,
  ...props
}: ITextInputProps) {
  'use memo';

  const helperTextId = useId();
  const charCountId = useId();
  const describedByHelperText = helperText ? helperTextId : null;
  const describedByCharCount = showCharCount && maxLength ? charCountId : null;
  const describedBy = [describedByHelperText, describedByCharCount].filter(Boolean).join(' ') || undefined;

  const [charCount, setCharCount] = useState(() => {
    const initial = value ?? defaultValue ?? '';
    return String(initial).length;
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCharCount(e.target.value.length);
    onChange?.(e);
  };

  return (
    <label className={classNames(styles.root, fullWidth && styles.fullWidth, containerClassName)}>
      {label && (
        <span className={styles.labelRow}>
          {label && <span className={classNames(styles.label, props.required && styles.required)}>{label}</span>}
          {labelSuffix && <span className={styles.labelSuffix}>{labelSuffix}</span>}
        </span>
      )}

      <div
        className={classNames(
          styles.inputWrapper,
          styles[variant],
          styles[size],
          error && styles.error,
          disabled && styles.disabled,
          wrapperClassName,
        )}
      >
        {startAdornment && <span className={styles.startAdornment}>{startAdornment}</span>}

        <input
          ref={forwardedRef}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          className={classNames(styles.input, className)}
          aria-describedby={describedBy}
          aria-invalid={error || undefined}
          onChange={handleChange}
          {...props}
        />

        {endAdornment && <span className={styles.endAdornment}>{endAdornment}</span>}
      </div>

      {(helperText || (showCharCount && maxLength)) && (
        <div className={styles.footer}>
          {helperText && (
            <p id={helperTextId} className={classNames(styles.helperText, error && styles.helperTextError)}>
              {helperText}
            </p>
          )}
          {showCharCount && maxLength && (
            <span
              id={charCountId}
              aria-live="polite"
              className={classNames(styles.charCount, charCount >= maxLength && styles.charCountLimit)}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}
    </label>
  );
}
